/**
 * Shipment alert-level utilities
 * Pure, framework-agnostic — safe to use in API, web, mobile and tests.
 *
 * An "alert level" describes how urgent a shipment card looks in the Kanban
 * board (and anywhere else we need visual urgency indicators).
 *
 * Levels (ascending severity):
 *   alert    → approaching a deadline — yellow
 *   warning  → deadline passed but within grace window — orange
 *   critical → well past deadline / action overdue — red
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type AlertLevel = "alert" | "warning" | "critical"

/**
 * Minimal shape required by shipmentAlertLevel().
 * All fields are optional/nullable so the function is safe with partial data.
 */
export interface ShipmentAlertInput {
  status:                        string
  plannedAt?:                    string | Date | null
  originArrivalAt?:              string | Date | null
  originLoadingAt?:              string | Date | null
  destinationArrivalAt?:         string | Date | null
  destinationUnloadingAt?:       string | Date | null
  finalizedAt?:                  string | Date | null
  scheduledOriginArrivalAt?:     string | Date | null
  scheduledDestinationArrivalAt?: string | Date | null
}

/**
 * Thresholds (in hours) for the "origin stale" alert on activo shipments.
 * Passed as a second argument to shipmentAlertLevel() — defaults to 2h/5h.
 */
export interface ShipmentAlertConfig {
  originStaleYellowHours?:      number
  originStaleRedHours?:         number
  destinationStaleYellowHours?: number
  destinationStaleRedHours?:    number
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TWO_HOURS_MS    = 2 * 60 * 60 * 1_000
const FIFTEEN_MIN_MS  = 15 * 60 * 1_000
const THIRTY_MIN_MS   = 30 * 60 * 1_000
const SIXTY_MIN_MS    = 60 * 60 * 1_000

/** Default thresholds for the origin-stale rule (Rule: activo + last origin event is stale). */
const DEFAULT_ORIGIN_STALE_YELLOW_HOURS = 2
const DEFAULT_ORIGIN_STALE_RED_HOURS    = 5

/** Default thresholds for the destination-stale rule (Rule: en_curso + arrived but not unloaded/finalized). */
const DEFAULT_DESTINATION_STALE_YELLOW_HOURS = 2
const DEFAULT_DESTINATION_STALE_RED_HOURS    = 5

// ─── Internal helpers ─────────────────────────────────────────────────────────

function toMs(value: string | Date | null | undefined): number | null {
  if (!value) return null
  const ms = typeof value === "string" ? new Date(value).getTime() : value.getTime()
  return isNaN(ms) ? null : ms
}

/**
 * Computes an alert level based on a scheduled timestamp vs. a confirmation
 * timestamp, using a symmetric ±window approach:
 *
 *   now < scheduled - window   → null   (plenty of time)
 *   now < scheduled            → alert  (approaching)
 *   now < scheduled + window   → warning (grace period)
 *   now >= scheduled + window  → critical (overdue)
 *
 * Returns null when already confirmed (confirmedAt is set) or when
 * scheduledAt is missing.
 */
function windowAlert(
  scheduledAt:  string | Date | null | undefined,
  confirmedAt:  string | Date | null | undefined,
  windowMs:     number,
  now:          number,
): AlertLevel | null {
  if (toMs(confirmedAt) !== null) return null   // already confirmed — no alert
  const scheduled = toMs(scheduledAt)
  if (scheduled === null) return null           // no deadline — no alert

  const diffMs = scheduled - now               // positive = future, negative = past
  if (diffMs < -windowMs) return "critical"
  if (diffMs < 0)         return "warning"
  if (diffMs < windowMs)  return "alert"
  return null
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns the alert level for a shipment given its current status and
 * relevant timestamps.
 *
 * Rules per status:
 *
 * **planificado**
 *   Uses `plannedAt` (set once when the shipment first enters this status).
 *   - >30 min without confirmation → warning
 *   - >60 min without confirmation → critical
 *   No "approaching" (alert) phase — the clock starts only after being planned.
 *
 * **activo**
 *   Two independent checks — the highest severity wins:
 *
 *   Rule A — scheduled arrival window:
 *     Uses `scheduledOriginArrivalAt` vs `originArrivalAt` (confirmation).
 *     - <2h to scheduled arrival   → alert
 *     - past scheduled arrival     → warning
 *     - >2h past scheduled arrival → critical
 *
 *   Rule B — origin stale (configurable thresholds via `config`):
 *     Applies when the shipment is still `activo` (driver has not departed from origin yet)
 *     and the most recent origin event (`originArrivalAt` or `originLoadingAt`) happened
 *     longer ago than the configured threshold. Only fires when `originDepartureAt` is null
 *     (i.e. the truck has not left the origin yet).
 *     - elapsed > originStaleYellowHours → alert  (yellow)
 *     - elapsed > originStaleRedHours    → critical (red)
 *
 * **en_curso**
 *   Uses `scheduledDestinationArrivalAt` vs `destinationArrivalAt`.
 *   Same ±2h window as activo Rule A.
 *
 * All other statuses → null.
 *
 * @example
 * ```ts
 * const level = shipmentAlertLevel(row)
 * // level: "alert" | "warning" | "critical" | null
 *
 * // With custom thresholds from app-config:
 * const level = shipmentAlertLevel(row, Date.now(), { originStaleYellowHours: 2, originStaleRedHours: 5 })
 * ```
 */
export function shipmentAlertLevel(
  shipment: ShipmentAlertInput,
  now: number = Date.now(),
  config: ShipmentAlertConfig = {},
): AlertLevel | null {
  switch (shipment.status) {
    case "planificado": {
      const plannedMs = toMs(shipment.plannedAt)
      if (plannedMs === null) return null
      const elapsedMs = now - plannedMs
      if (elapsedMs > SIXTY_MIN_MS)   return "critical"
      if (elapsedMs > THIRTY_MIN_MS)  return "warning"
      if (elapsedMs > FIFTEEN_MIN_MS) return "alert"
      return null
    }

    case "activo": {
      // Rule A: scheduled arrival window
      const ruleA = windowAlert(
        shipment.scheduledOriginArrivalAt,
        shipment.originArrivalAt,
        TWO_HOURS_MS,
        now,
      )

      // Rule B: origin stale — most recent origin event vs. configured thresholds.
      // Only applies when the truck has NOT yet departed from origin
      // (originDepartureAt is not part of ShipmentAlertInput — we rely on the
      //  shipment still being "activo", which implies no departure has been recorded).
      const ruleB = originStaleLevel(shipment, now, config)

      // Return the highest severity between the two rules
      return maxLevel(ruleA, ruleB)
    }

    case "en_curso": {
      // Rule A: scheduled destination arrival window (fires when not yet arrived)
      const ruleA = windowAlert(
        shipment.scheduledDestinationArrivalAt,
        shipment.destinationArrivalAt,
        TWO_HOURS_MS,
        now,
      )

      // Rule B: destination stale — arrived but neither unloaded nor finalized.
      const ruleB = destinationStaleLevel(shipment, now, config)

      return maxLevel(ruleA, ruleB)
    }

    default:
      return null
  }
}

// ─── Internal: origin stale rule ─────────────────────────────────────────────

/**
 * Severity order for comparison.
 * Higher index = higher severity.
 */
const LEVEL_RANK: Record<AlertLevel, number> = { alert: 1, warning: 2, critical: 3 }

/** Returns the higher-severity of two nullable levels. */
function maxLevel(a: AlertLevel | null, b: AlertLevel | null): AlertLevel | null {
  if (!a) return b
  if (!b) return a
  return LEVEL_RANK[a] >= LEVEL_RANK[b] ? a : b
}

/**
 * Rule B — destination stale:
 * Fires when the shipment has arrived at destination but is not yet finalized.
 * Measures elapsed time since the most recent destination event:
 *   - If `destinationUnloadingAt` exists → measure from there (truck is unloaded but not finalized)
 *   - Otherwise → measure from `destinationArrivalAt` (truck arrived, nothing recorded yet)
 * Clears only when `finalizedAt` is set.
 */
function destinationStaleLevel(
  shipment: ShipmentAlertInput,
  now: number,
  config: ShipmentAlertConfig,
): AlertLevel | null {
  if (!toMs(shipment.destinationArrivalAt)) return null  // not arrived yet
  if (toMs(shipment.finalizedAt))           return null  // already finalized

  const yellowMs = (config.destinationStaleYellowHours ?? DEFAULT_DESTINATION_STALE_YELLOW_HOURS) * 60 * 60 * 1_000
  const redMs    = (config.destinationStaleRedHours    ?? DEFAULT_DESTINATION_STALE_RED_HOURS)    * 60 * 60 * 1_000

  // Use the most recent destination event as the reference point
  const arrivalMs   = toMs(shipment.destinationArrivalAt)!
  const unloadingMs = toMs(shipment.destinationUnloadingAt)
  const lastEventMs = unloadingMs !== null ? Math.max(arrivalMs, unloadingMs) : arrivalMs

  const elapsed = now - lastEventMs
  if (elapsed <= 0)        return null
  if (elapsed >= redMs)    return "critical"
  if (elapsed >= yellowMs) return "alert"
  return null
}

/**
 * Rule B — origin stale:
 * Takes the most recent timestamp between `originArrivalAt` and `originLoadingAt`.
 * If it exists and the elapsed time since that event exceeds the configured
 * threshold, returns an alert level. Returns null if no origin event is recorded.
 */
function originStaleLevel(
  shipment: ShipmentAlertInput,
  now: number,
  config: ShipmentAlertConfig,
): AlertLevel | null {
  const yellowMs = (config.originStaleYellowHours ?? DEFAULT_ORIGIN_STALE_YELLOW_HOURS) * 60 * 60 * 1_000
  const redMs    = (config.originStaleRedHours    ?? DEFAULT_ORIGIN_STALE_RED_HOURS)    * 60 * 60 * 1_000

  const arrivalMs = toMs(shipment.originArrivalAt)
  const loadingMs = toMs(shipment.originLoadingAt)

  // Pick the most recent origin event
  let lastEventMs: number | null = null
  if (arrivalMs !== null && loadingMs !== null) {
    lastEventMs = Math.max(arrivalMs, loadingMs)
  } else {
    lastEventMs = arrivalMs ?? loadingMs
  }

  if (lastEventMs === null) return null   // no origin event yet — rule does not apply

  const elapsedMs = now - lastEventMs
  if (elapsedMs <= 0)       return null   // event is in the future (clock skew guard)
  if (elapsedMs >= redMs)   return "critical"
  if (elapsedMs >= yellowMs) return "alert"
  return null
}

// ─── UI helpers (framework-agnostic) ─────────────────────────────────────────

/**
 * Tailwind class sets for each alert level.
 * Import in any component — no React dependency.
 */
export const ALERT_LEVEL_CARD_CLASSES: Record<AlertLevel, string> = {
  alert:    "bg-yellow-50 border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-900/50",
  warning:  "bg-orange-50 border-orange-200 dark:bg-orange-950/20 dark:border-orange-900/50",
  critical: "bg-red-50    border-red-200    dark:bg-red-950/20    dark:border-red-900/50",
}

export const ALERT_LEVEL_BADGE_CLASSES: Record<AlertLevel, string> = {
  alert:    "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400",
  warning:  "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400",
  critical: "bg-red-100    text-red-700    dark:bg-red-900/40    dark:text-red-400",
}

/** Human-readable label for a badge, given the status column context. */
export function alertBadgeLabel(
  count: number,
  status: string,
): string {
  if (status === "planificado") return `${count} demorada${count !== 1 ? "s" : ""}`
  return `${count} en alerta`
}

/** Tooltip text for the column header badge. */
export function alertBadgeTooltip(status: string): string {
  if (status === "planificado") return "Planificadas hace más de 30 min sin confirmar"
  if (status === "activo")      return "Sin llegada o carga en origen demorada, o cerca/pasada la fecha proyectada"
  return "Sin llegada a destino — cerca o pasada la fecha proyectada"
}
